import { useAuth0 } from "@auth0/auth0-react"
import { useQuery } from "@tanstack/react-query"
import useLogging from "../hooks/useLogging"
import { getDateForString } from "../utils/generalUtils"
import useFetchSettings from "./useFetchSettings"

const BATCH_SIZE = 10 // ~50 days per batch

const getBatchData = async (token, dateRange) => {
	const batchData = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kpi-dashboard`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json"
		},
		body: JSON.stringify({ dateRanges: [dateRange] })
	})

	const batch = await batchData.json()

	return batch.payload
}

// Fetch ROI metrics data
export const fetchRoiMetrics = async (token, logging, settingsData) => {
	try {
		// Define date ranges to fetch in parallel
		// First fetch all available dates from the API
		const datesResponse = await fetch(
			`${process.env.REACT_APP_BACKEND_URL}/api/list-roi-metrics-dates`,
			{
				headers: {
					Authorization: `Bearer ${token}`
				}
			}
		)
		const datesData = await datesResponse.json()
		const availableDates = datesData.payload.map(item => item.date)

		logging.info("UseFetchRoiMetrics - Available dates:", {
			component: "useFetchRoiMetrics",
			function: "fetchRoiMetrics",
			availableDates: availableDates.length,
			settingsLiveDate: settingsData?.liveDate,
			fallbackDate: "2024-03-16"
		})

		const endDate = new Date()
		const startDate =
			availableDates.length > 0
				? getDateForString(availableDates[availableDates.length - 1])
				: settingsData?.liveDate
					? new Date(settingsData.liveDate)
					: new Date("2024-03-16") // 2 years ago fallback

		logging.info("UseFetchRoiMetrics - Date determination:", {
			component: "useFetchRoiMetrics",
			function: "fetchRoiMetrics",
			availableDatesExist: availableDates.length > 0,
			chosenStartDate: startDate.toISOString(),
			liveDateExists: !!settingsData?.liveDate,
			totalAvailableDates: availableDates.length
		})

		// Create date range batches
		const dateRanges = []
		let currentEnd = new Date(endDate)

		while (currentEnd >= startDate) {
			const batchStart = new Date(currentEnd)
			batchStart.setDate(batchStart.getDate() - BATCH_SIZE)

			// Ensure we don't go before the start date
			const rangeStart = batchStart > startDate ? batchStart : startDate

			dateRanges.push({
				start_date: rangeStart.toISOString().split("T")[0],
				end_date: currentEnd.toISOString().split("T")[0]
			})

			// Move to the next batch
			currentEnd = new Date(rangeStart)
			currentEnd.setDate(currentEnd.getDate() - 1)
		}

		if (!dateRanges.length) {
			logging.warn("useFetchRoiMetrics - Fetching ROI metrics - No date ranges created", {
				component: "useFetchRoiMetrics",
				function: "fetchRoiMetrics"
			})
		}

		const roiMetrics = []
		const CONCURRENT_LIMIT = 5

		for (let i = 0; i < dateRanges.length; i += CONCURRENT_LIMIT) {
			const batch = dateRanges.slice(i, i + CONCURRENT_LIMIT)
			const results = await Promise.all(batch.map(r => getBatchData(token, r)))
			for (const res of results) roiMetrics.push(...res)
		}

		// Remove duplicates (in case date ranges overlapped)
		const uniqueMetrics = []
		const seenDates = new Set()

		for (const metric of roiMetrics) {
			if (!seenDates.has(metric.date)) {
				seenDates.add(metric.date)
				uniqueMetrics.push(metric)
			} else {
				logging.warn("Duplicate ROI metric found for date", {
					date: metric.date,
					component: "useFetchRoiMetrics",
					function: "fetchRoiMetrics"
				})
			}
		}

		// Sort by date
		uniqueMetrics.sort((a, b) => getDateForString(a.date) - getDateForString(b.date))

		const filteredMetrics = uniqueMetrics.filter(
			metric => metric.patient_volumes?.completed > 0
		)

		return filteredMetrics
	} catch (error) {
		logging.error("Error fetching ROI metrics:", {
			component: "useFetchRoiMetrics",
			function: "fetchRoiMetrics",
			error: error.message,
			errorStack: error.stack
		})
		throw error
	}
}

/**
 * This hook fetches the ROI metrics data
 * @returns {roiMetrics, refetch: function}
 */
export const useFetchRoiMetrics = () => {
	const { getAccessTokenSilently } = useAuth0()
	const logging = useLogging()
	const { settingsData } = useFetchSettings()
	const { data: roiMetrics, refetch } = useQuery({
		queryKey: ["roiMetrics"],
		queryFn: async () => {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			return fetchRoiMetrics(token, logging, settingsData)
		},
		// Data is considered fresh forever (never goes stale automatically)
		staleTime: Infinity,
		// Disable automatic refetching
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
		// Only fetch once when the component mounts
		enabled: true,
		// Add these to help with debugging
		onSuccess: data => {
			logging.info("Query succeeded with", data?.length, "metrics")
		},
		onError: error => {
			logging.error("Query failed:", error)
		}
	})

	return { roiMetrics, refetch }
}

export default useFetchRoiMetrics
